import { useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import type {
  CustomerFormCustomer,
  CustomerFormData,
  ContactPerson,
} from './types';

/**
 * The unique index on the customer's email address, case and whitespace
 * insensitive. Postgres reports a clash as code 23505 naming this index, and
 * the raw text ("duplicate key value violates unique constraint...") is unfit
 * for a user. Mirrors supabase/functions/_shared/customer-email-error.ts so
 * this save path says the same thing the edge functions say.
 */
const EMAIL_INDEX_NAME = 'customers_email_lower_unique_idx';

/** The shape a PostgREST failure arrives in. It is a plain object, NOT an Error. */
interface PostgresLikeError {
  code?: string | null;
  message?: string;
  details?: string | null;
}

function asPostgresError(err: unknown): PostgresLikeError | null {
  return (err && typeof err === 'object') ? err as PostgresLikeError : null;
}

function isDuplicateEmailError(err: unknown): boolean {
  const e = asPostgresError(err);
  if (!e || e.code !== '23505') return false;
  return `${e.message ?? ''} ${e.details ?? ''}`.includes(EMAIL_INDEX_NAME);
}

/**
 * Turn a save failure into something worth reading. Two traps live here:
 * a PostgREST error is not an Error instance, so the old
 * `err instanceof Error ? err.message : 'Unknown error'` showed every database
 * rejection as "Unknown error"; and a duplicate email is usually a deleted
 * customer who still holds the address, which the user cannot see from the
 * screen. Names them where we can.
 */
async function describeSaveError(
  supabase: SupabaseClient,
  err: unknown,
  email: string,
): Promise<string> {
  if (isDuplicateEmailError(err)) {
    const normalised = email.trim().toLowerCase();

    const { data } = await supabase
      .from('customers')
      .select('customer_name, status')
      .ilike('email', normalised)
      .limit(1)
      .maybeSingle();

    if (!data) {
      return `A customer already uses ${normalised}. Search for them above, or use a different email address.`;
    }

    return data.status === 'inactive'
      ? `${data.customer_name} was deleted but still holds ${normalised}. Clear the email field and type it again to bring them back, or use a different email address.`
      : `${data.customer_name} already uses ${normalised}. Search for them above and edit that record, or use a different email address.`;
  }

  const pgError = asPostgresError(err);
  if (pgError?.message) return pgError.message;
  if (err instanceof Error) return err.message;
  return 'The customer could not be saved. Please try again.';
}

interface UseCustomerSaveOptions {
  supabase: SupabaseClient;
  formData: CustomerFormData;
  selectedCustomer: CustomerFormCustomer | null;
  duplicateCustomer: CustomerFormCustomer | null;
  originalContactPersons: ContactPerson[];
  defaultCountry: string;
  onSave: (customer: CustomerFormCustomer) => void;
  toggleSection: (section: string) => void;
}

export function useCustomerSave({
  supabase,
  formData,
  selectedCustomer,
  duplicateCustomer,
  originalContactPersons,
  defaultCountry,
  onSave,
  toggleSection,
}: UseCustomerSaveOptions) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function validate(): string | null {
    if (!formData.email.trim()) {
      return 'Email is required';
    }
    if (!formData.email.includes('@')) {
      return 'Please enter a valid email address';
    }

    if (formData.customer_type === 'individual') {
      if (!formData.first_name.trim()) {
        return 'First name is required';
      }
      if (!formData.last_name.trim()) {
        return 'Last name is required';
      }
    }

    if (formData.customer_type === 'business' && !formData.company_name.trim()) {
      return 'Company name is required for business customers';
    }

    if (!formData.customer_name.trim()) {
      return 'Display name is required';
    }

    if (duplicateCustomer && !selectedCustomer) {
      return duplicateCustomer.status === 'inactive'
        ? `${duplicateCustomer.customer_name} was deleted but still holds "${formData.email}". Click "Restore & Load" to bring them back, or use a different email address.`
        : `A customer with email "${formData.email}" already exists. Click "Load Details" to edit them.`;
    }

    if (formData.contact_email.trim() &&
        formData.contact_email.trim().toLowerCase() === formData.email.trim().toLowerCase()) {
      toggleSection('contact');
      return 'Contact person email must be different from customer email';
    }

    return null;
  }

  function buildPayload() {
    const emptyToNull = (val: string | number | boolean | null | undefined) => (val === '' || val === undefined) ? null : val;

    const contactPersons = [];

    const findContactPersonId = (email: string) => {
      if (!email || !originalContactPersons.length) return undefined;
      const match = originalContactPersons.find(
        cp => cp.email?.toLowerCase() === email.toLowerCase()
      );
      return match?.contact_person_id;
    };

    const primaryEmail = formData.email.trim();
    const primaryContactPersonId = findContactPersonId(primaryEmail);
    contactPersons.push({
      first_name: formData.first_name.trim(),
      last_name: formData.last_name.trim(),
      email: primaryEmail,
      phone: formData.phone.trim(),
      mobile: formData.mobile.trim(),
      is_primary_contact: true,
      ...(primaryContactPersonId && { contact_person_id: primaryContactPersonId }),
    });

    const hasSecondaryContact = formData.contact_first_name.trim() ||
                                formData.contact_last_name.trim() ||
                                formData.contact_email.trim() ||
                                formData.contact_phone.trim() ||
                                formData.contact_mobile.trim();

    if (hasSecondaryContact) {
      const secondaryEmail = formData.contact_email.trim();
      const secondaryContactPersonId = findContactPersonId(secondaryEmail);
      contactPersons.push({
        first_name: formData.contact_first_name.trim(),
        last_name: formData.contact_last_name.trim(),
        email: secondaryEmail,
        phone: formData.contact_phone.trim(),
        mobile: formData.contact_mobile.trim(),
        designation: formData.contact_designation.trim(),
        department: formData.contact_department.trim(),
        is_primary_contact: false,
        ...(secondaryContactPersonId && { contact_person_id: secondaryContactPersonId }),
      });
    }

    return {
      customer_type: formData.customer_type,
      status: formData.status,
      customer_name: formData.customer_name.trim(),
      company_name: emptyToNull(formData.company_name.trim()),
      email: formData.email.trim(),
      phone: emptyToNull(formData.phone.trim()),
      mobile: emptyToNull(formData.mobile.trim()),
      website: emptyToNull(formData.website.trim()),
      first_name: formData.first_name.trim() || undefined,
      last_name: formData.last_name.trim() || undefined,
      contact_persons: contactPersons.length > 0 ? contactPersons : undefined,
      billing_address: {
        attention: formData.billing_address.attention.trim(),
        address: formData.billing_address.address.trim(),
        street2: formData.billing_address.street2.trim(),
        suburb: formData.billing_address.suburb.trim(),
        city: formData.billing_address.city.trim(),
        state: formData.billing_address.state.trim(),
        zip: formData.billing_address.zip.trim(),
        country: formData.billing_address.country.trim() || defaultCountry,
      },
      // When 'same as billing' is ticked, copy billing into shipping so the
      // saved record reflects the user's intent. Previously this was set to
      // `undefined` which left shipping_address stale or NULL — a silent bug
      // because the toggle implied the addresses were now identical.
      shipping_address: formData.same_shipping
        ? {
            attention: formData.billing_address.attention.trim(),
            address: formData.billing_address.address.trim(),
            street2: formData.billing_address.street2.trim(),
            suburb: formData.billing_address.suburb.trim(),
            city: formData.billing_address.city.trim(),
            state: formData.billing_address.state.trim(),
            zip: formData.billing_address.zip.trim(),
            country: formData.billing_address.country.trim() || defaultCountry,
          }
        : {
            attention: formData.shipping_address.attention.trim(),
            address: formData.shipping_address.address.trim(),
            street2: formData.shipping_address.street2.trim(),
            suburb: formData.shipping_address.suburb.trim(),
            city: formData.shipping_address.city.trim(),
            state: formData.shipping_address.state.trim(),
            zip: formData.shipping_address.zip.trim(),
            country: formData.shipping_address.country.trim() || defaultCountry,
          },
      payment_terms: emptyToNull(formData.payment_terms),
      credit_limit: emptyToNull(formData.credit_limit),
      vat_reg_no: emptyToNull(formData.vat_reg_no.trim()),
      vat_treatment: emptyToNull(formData.vat_treatment),
      is_taxable: formData.is_taxable,
      is_pos: formData.is_pos,
      hospitality_notes: emptyToNull(formData.hospitality_notes.trim()),
      notes: emptyToNull(formData.notes.trim()),
    };
  }

  async function handleSave() {
    setError(null);

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);

    try {
      const payload = buildPayload();

      let savedCustomer;

      // A lead-sourced selection is NOT an existing customer — the form was
      // pre-filled from a leads row whose id can't be used to update customers.
      // Insert a fresh customer, then mark the lead converted so it drops out
      // of future searches.
      const isLeadSource = (selectedCustomer as { source?: string } | null)?.source === 'lead';
      const leadId = (selectedCustomer as { lead_id?: string; id?: string } | null)?.lead_id
        || (isLeadSource ? (selectedCustomer as { id?: string } | null)?.id : undefined);

      if (selectedCustomer && !isLeadSource) {
        const { data, error: updateError } = await supabase
          .from('customers')
          .update(payload)
          .eq('id', selectedCustomer.id)
          .select()
          .single();

        if (updateError) throw updateError;
        savedCustomer = data;
      } else {
        const { data, error: insertError } = await supabase
          .from('customers')
          .insert(payload)
          .select()
          .single();

        if (insertError) throw insertError;
        savedCustomer = data;

        // Fire-and-forget conversion of the originating lead. Failure logs
        // but does not block the save — the customer is already in.
        if (isLeadSource && leadId && savedCustomer?.id) {
          void supabase
            .from('leads')
            .update({
              status: 'converted',
              customer_id: savedCustomer.id,
              converted_at: new Date().toISOString(),
              status_changed_at: new Date().toISOString(),
            })
            .eq('id', leadId)
            .is('customer_id', null)
            .then(({ error }) => {
              if (error) console.warn('[useCustomerSave] could not mark lead converted:', error.message);
            });
        }
      }

      if (!savedCustomer) {
        throw new Error('No customer data returned');
      }

      onSave(savedCustomer);

    } catch (err) {
      console.error('Save error:', err);
      setError(await describeSaveError(supabase, err, formData.email));
    } finally {
      setSaving(false);
    }
  }

  return {
    saving,
    error,
    setError,
    handleSave,
  };
}
